import { useState } from "react";

type Tone = "accent" | "danger" | "info" | "warn";

interface InteractiveMetric {
  id: string;
  value: string;
  label: string;
  detail: string;
  tone: Tone;
}

interface BarDatum {
  id: string;
  label: string;
  value: number;
  detail: string;
}

const toneClasses: Record<Tone, string> = {
  accent: "border-accent/40 bg-accent/10 text-accent",
  danger: "border-danger/40 bg-danger/10 text-danger",
  info: "border-line/35 bg-elevated/65 text-ink",
  warn: "border-warn/40 bg-warn/10 text-warn",
};

function MetricExplorer({
  metrics,
  ariaLabel,
}: {
  metrics: InteractiveMetric[];
  ariaLabel: string;
}) {
  const [activeId, setActiveId] = useState(metrics[0].id);
  const active = metrics.find((metric) => metric.id === activeId) ?? metrics[0];

  return (
    <div>
      <div
        className={`grid gap-3 ${
          metrics.length === 4 ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-3"
        }`}
        aria-label={ariaLabel}
      >
        {metrics.map((metric) => (
          <button
            key={metric.id}
            type="button"
            aria-pressed={metric.id === activeId}
            aria-label={`${metric.value}, ${metric.label}. ${metric.detail}`}
            onClick={() => setActiveId(metric.id)}
            onFocus={() => setActiveId(metric.id)}
            onMouseEnter={() => setActiveId(metric.id)}
            className={[
              "min-h-32 rounded-xl border p-4 text-left transition duration-200",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              toneClasses[metric.tone],
              metric.id === activeId
                ? "-translate-y-0.5 border-current bg-opacity-20"
                : "opacity-80 hover:-translate-y-0.5 hover:opacity-100",
            ].join(" ")}
          >
            <span className="block font-display text-3xl font-semibold">
              {metric.value}
            </span>
            <span className="mt-2 block text-sm font-semibold text-ink">
              {metric.label}
            </span>
          </button>
        ))}
      </div>
      <p
        className="mt-3 min-h-14 rounded-lg border border-line/20 bg-bg/45 px-4 py-3 text-sm leading-relaxed text-muted"
        aria-live="polite"
      >
        <strong className="text-ink">{active.label}:</strong> {active.detail}
      </p>
    </div>
  );
}

function InteractiveBars({
  data,
  max,
  suffix = "%",
  reference,
  referenceLabel,
  ariaLabel,
}: {
  data: BarDatum[];
  max: number;
  suffix?: string;
  reference?: number;
  referenceLabel?: string;
  ariaLabel: string;
}) {
  const [activeId, setActiveId] = useState(data[0].id);
  const active = data.find((datum) => datum.id === activeId) ?? data[0];

  return (
    <div>
      <div className="space-y-3" aria-label={ariaLabel}>
        {data.map((datum) => {
          const width = Math.min(100, (datum.value / max) * 100);
          return (
            <button
              key={datum.id}
              type="button"
              aria-pressed={datum.id === activeId}
              aria-label={`${datum.label}: ${datum.value}${suffix}. ${datum.detail}`}
              onClick={() => setActiveId(datum.id)}
              onFocus={() => setActiveId(datum.id)}
              onMouseEnter={() => setActiveId(datum.id)}
              className="group block w-full rounded-lg text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <span className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium text-ink">{datum.label}</span>
                <span className="font-semibold tabular-nums text-accent">
                  {datum.value}
                  {suffix}
                </span>
              </span>
              <span className="relative block h-3 overflow-hidden rounded-full bg-elevated">
                {reference !== undefined ? (
                  <span
                    className="absolute inset-y-0 z-10 w-px bg-ink/55"
                    style={{ left: `${(reference / max) * 100}%` }}
                    aria-hidden="true"
                  />
                ) : null}
                <span
                  className={[
                    "block h-full origin-left rounded-full bg-accent transition duration-300",
                    datum.id === activeId
                      ? "opacity-100"
                      : "opacity-55 group-hover:opacity-100",
                  ].join(" ")}
                  style={{ width: `${width}%` }}
                  aria-hidden="true"
                />
              </span>
            </button>
          );
        })}
      </div>
      {reference !== undefined && referenceLabel ? (
        <p className="mt-2 text-right text-[11px] text-faint">
          Marker: {reference}
          {suffix} · {referenceLabel}
        </p>
      ) : null}
      <p
        className="mt-3 min-h-14 border-t border-line/20 pt-3 text-sm leading-relaxed text-muted"
        aria-live="polite"
      >
        <strong className="text-ink">{active.label}:</strong> {active.detail}
      </p>
    </div>
  );
}

const gapMetrics: InteractiveMetric[] = [
  {
    id: "agreement",
    value: "3",
    label: "Days all three indices agree",
    detail:
      "TM90, DG83 and S04 all identified blocking on the same day only three times.",
    tone: "danger",
  },
  {
    id: "days",
    value: "3,680",
    label: "European summer days",
    detail:
      "Forty JJA seasons of daily ERA5 snapshots from 1979–2018 formed the thesis dataset.",
    tone: "info",
  },
  {
    id: "trend",
    value: "+3 / decade",
    label: "Observed dipole-frequency trend",
    detail:
      "Double-jet configurations increased by about three days per decade from 1980 in Kornhuber & Coumou (2022).",
    tone: "warn",
  },
];

const overlapData: BarDatum[] = [
  {
    id: "tm90-dg83",
    label: "TM90 ∩ DG83",
    value: 3.1,
    detail:
      "The Z500-gradient and Z500-anomaly methods overlap on only 3.1% of identified cases.",
  },
  {
    id: "dg83-s04",
    label: "DG83 ∩ S04",
    value: 4.9,
    detail:
      "The Z500-anomaly and potential-vorticity methods overlap on 4.9% of cases.",
  },
  {
    id: "tm90-s04",
    label: "TM90 ∩ S04",
    value: 7.4,
    detail:
      "Even the largest pairwise overlap, between TM90 and S04, reaches only 7.4%.",
  },
];

export function ScientificGapGraphic() {
  return (
    <figure
      className="panel mt-6 overflow-hidden p-4 sm:p-5"
      aria-labelledby="scientific-gap-graphic-title"
    >
      <figcaption className="mb-5">
        <p id="scientific-gap-graphic-title" className="font-display text-xl font-semibold text-ink">
          Three definitions, very little agreement
        </p>
        <p className="mt-1 text-sm text-muted">
          Hover, focus, or tap a metric to inspect it.
        </p>
      </figcaption>

      <MetricExplorer metrics={gapMetrics} ariaLabel="Scientific gap headline metrics" />

      <div className="mt-5 grid gap-5 border-t border-line/20 pt-5 md:grid-cols-[1.15fr_0.85fr]">
        <div>
          <h3 className="text-sm font-semibold text-ink">
            Pairwise index overlap
          </h3>
          <p className="mt-1 text-xs text-faint">
            Probability that two standard methods flag the same block
          </p>
          <div className="mt-4">
            <InteractiveBars
              data={overlapData}
              max={10}
              ariaLabel="Pairwise blocking-index overlap"
            />
          </div>
        </div>
        <aside className="rounded-xl border border-warn/30 bg-warn/10 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-warn">
            Why it matters
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            A detector trained against one index inherits that method&apos;s blind
            spots. The thesis therefore combined all three methods with expert
            validation into a Ground Truth Dataset.
          </p>
          <blockquote className="mt-4 border-l-2 border-warn/50 pl-3 text-sm italic leading-relaxed text-ink">
            “No one really knows what the fundamental mechanism for blocking is.”
            <footer className="mt-2 text-xs not-italic text-faint">
              — Lei Wang, Purdue University
            </footer>
          </blockquote>
        </aside>
      </div>
    </figure>
  );
}

const thesisMetrics: InteractiveMetric[] = [
  {
    id: "f1",
    value: "0.72",
    label: "CNN F1 score",
    detail:
      "Best held-out score balancing precision and recall on an imbalanced blocking dataset.",
    tone: "accent",
  },
  {
    id: "recall",
    value: "78%",
    label: "GTD recall",
    detail:
      "Share of expert-validated blocked days recovered on the unseen final ten years.",
    tone: "accent",
  },
  {
    id: "summers",
    value: "40",
    label: "European summers",
    detail:
      "Daily ERA5 data for June–August 1979–2018 were used for training and validation.",
    tone: "info",
  },
  {
    id: "blocked",
    value: "566",
    label: "GTD blocked days",
    detail:
      "Expert-validated labels combined TM90, DG83 and S04 with manual review.",
    tone: "info",
  },
];

const recoveryData: BarDatum[] = [
  {
    id: "tm90",
    label: "TM90",
    value: 55,
    detail: "The CNN reproduced 55% of TM90 Z500-gradient blocking events.",
  },
  {
    id: "dg83",
    label: "DG83",
    value: 78,
    detail: "The CNN reproduced 78% of DG83 Z500-anomaly blocking events.",
  },
  {
    id: "s04",
    label: "S04",
    value: 64,
    detail: "The CNN reproduced 64% of S04 potential-vorticity blocking events.",
  },
  {
    id: "gtd",
    label: "Ground truth",
    value: 78,
    detail: "The CNN recovered about 78% of expert-validated GTD events.",
  },
];

const contributionData: BarDatum[] = [
  {
    id: "gtd-dg83",
    label: "DG83 · Z500 anomaly",
    value: 70.8,
    detail: "DG83 contributed evidence to 70.8% of GTD labels.",
  },
  {
    id: "gtd-tm90",
    label: "TM90 · Z500 gradient",
    value: 22.5,
    detail: "TM90 contributed evidence to 22.5% of GTD labels.",
  },
  {
    id: "gtd-s04",
    label: "S04 · PV anomaly",
    value: 22.2,
    detail: "S04 contributed evidence to 22.2% of GTD labels.",
  },
];

export function ThesisResultsGraphic() {
  return (
    <figure
      className="panel mt-6 overflow-hidden p-4 sm:p-5"
      aria-labelledby="thesis-results-graphic-title"
    >
      <figcaption className="mb-5">
        <p id="thesis-results-graphic-title" className="font-display text-xl font-semibold text-ink">
          From conflicting indices to one validated detector
        </p>
        <p className="mt-1 text-sm text-muted">
          Held-out thesis results · hover, focus, or tap to explore.
        </p>
      </figcaption>

      <MetricExplorer metrics={thesisMetrics} ariaLabel="MSci thesis headline results" />

      <div className="mt-5 grid gap-6 border-t border-line/20 pt-5 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold text-ink">
            CNN recovery by reference method
          </h3>
          <p className="mt-1 text-xs text-faint">
            Share of each method&apos;s events reproduced on held-out data
          </p>
          <div className="mt-4">
            <InteractiveBars
              data={recoveryData}
              max={100}
              reference={50}
              referenceLabel="thesis significance threshold"
              ariaLabel="CNN event recovery rates"
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink">
            Evidence used in the Ground Truth Dataset
          </h3>
          <p className="mt-1 text-xs text-faint">
            Methods can overlap; these percentages are not parts of one whole
          </p>
          <div className="mt-4">
            <InteractiveBars
              data={contributionData}
              max={100}
              ariaLabel="Ground Truth Dataset method contributions"
            />
          </div>

          <div className="mt-4 rounded-xl border border-line/25 bg-bg/45 p-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="font-display text-3xl font-semibold text-warn">35</p>
                <p className="text-sm text-muted">CNN-only event groups</p>
              </div>
              <div className="text-right">
                <p className="font-display text-2xl font-semibold text-accent">14</p>
                <p className="text-sm text-muted">at onset or decay</p>
              </div>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-elevated">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${(14 / 35) * 100}%` }}
                aria-hidden="true"
              />
            </div>
            <p className="mt-2 text-xs text-faint">
              40% of CNN-only groups appeared around block formation or breakdown.
            </p>
          </div>
        </div>
      </div>
    </figure>
  );
}
